import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { AuthService } from '../../core/auth/auth.service';
import {
  CompanyDetailResponse,
  CompanySummary,
  CreateCompanyRequest,
  CreateDriverRequest,
  DriverGroupOption
} from '../../core/models/portal.models';

const EMPTY_DRIVER_FORM: Omit<CreateDriverRequest, 'companyId'> = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  personnelNumber: '',
  groupIds: [],
  preferredLanguage: 'de',
  dateOfBirth: null,
  licenseValidUntil: null,
  pScheinValidUntil: null,
  statusValidUntil: null,
  status: 'ACTIVE'
};

@Component({
  selector: 'app-company-detail-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './company-detail-page.component.html'
})
export class CompanyDetailPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  protected readonly detail = signal<CompanyDetailResponse | null>(null);
  protected readonly editDialogOpen = signal(false);
  protected readonly form = signal<CreateCompanyRequest>(this.emptyForm());
  protected readonly saving = signal(false);
  protected readonly saveError = signal<string | null>(null);
  protected readonly canEdit = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN']));
  protected readonly canDelete = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN']));
  protected readonly canCreateDriver = computed(() =>
    this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'COMPANY_ADMIN', 'QUALITY_MANAGER'])
  );
  protected readonly createDriverDialogOpen = signal(false);
  protected readonly driverForm = signal<Omit<CreateDriverRequest, 'companyId'>>(this.emptyDriverForm());
  protected readonly driverGroups = signal<DriverGroupOption[]>([]);
  protected readonly driverSaving = signal(false);
  protected readonly driverCreateError = signal<string | null>(null);

  private companyId = 0;

  ngOnInit(): void {
    this.companyId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
  }

  protected load(): void {
    this.api.company(this.companyId).subscribe((detail) => this.detail.set(detail));
  }

  protected openEditDialog(company: CompanySummary): void {
    this.saveError.set(null);
    this.form.set(this.formFromCompany(company));
    this.editDialogOpen.set(true);
  }

  protected closeEditDialog(): void {
    if (this.saving()) {
      return;
    }
    this.editDialogOpen.set(false);
    this.saveError.set(null);
  }

  protected saveCompany(event: Event): void {
    event.preventDefault();
    const request = this.normalizedRequest();

    if (!request.name) {
      this.saveError.set('Bitte gib einen Unternehmensnamen ein.');
      return;
    }

    this.saving.set(true);
    this.saveError.set(null);

    this.api.updateCompany(this.companyId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.editDialogOpen.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.saveError.set('Der Unternehmer konnte nicht gespeichert werden.');
      }
    });
  }

  protected openCreateDriverDialog(): void {
    this.driverCreateError.set(null);
    this.driverForm.set(this.emptyDriverForm());
    this.loadDriverGroups();
    this.createDriverDialogOpen.set(true);
  }

  protected closeCreateDriverDialog(): void {
    if (this.driverSaving()) {
      return;
    }
    this.createDriverDialogOpen.set(false);
    this.driverCreateError.set(null);
  }

  protected createDriver(event: Event): void {
    event.preventDefault();
    const request = this.normalizedDriverRequest();

    if (!request.firstName) {
      this.driverCreateError.set('Bitte gib einen Vornamen ein.');
      return;
    }

    if (!request.lastName) {
      this.driverCreateError.set('Bitte gib einen Nachnamen ein.');
      return;
    }

    if (!request.email) {
      this.driverCreateError.set('Bitte gib eine E-Mail-Adresse ein.');
      return;
    }

    this.driverSaving.set(true);
    this.driverCreateError.set(null);

    this.api.createDriver(request).subscribe({
      next: () => {
        this.driverSaving.set(false);
        this.createDriverDialogOpen.set(false);
        this.load();
      },
      error: () => {
        this.driverSaving.set(false);
        this.driverCreateError.set('Der Fahrer konnte nicht angelegt werden.');
      }
    });
  }

  protected deleteCompany(company: CompanySummary): void {
    if (!window.confirm(`Unternehmer "${company.name}" wirklich löschen? Zugehörige Fahrer und Benutzer werden entfernt.`)) {
      return;
    }

    this.api.deleteCompany(company.id).subscribe(() => this.router.navigate(['/companies']));
  }

  protected updateForm(field: keyof CreateCompanyRequest, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
    this.form.update((current) => ({ ...current, [field]: value }));
  }

  protected updateNumberForm(field: keyof CreateCompanyRequest, event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.form.update((current) => ({ ...current, [field]: raw === '' ? null : Number(raw) }));
  }

  protected updateDriverForm(field: keyof Omit<CreateDriverRequest, 'companyId'>, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement).value;
    this.driverForm.update((current) => ({ ...current, [field]: value }));
  }

  protected updateDriverCompany(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    this.driverForm.update((current) => ({ ...current, companyId: value, groupIds: [] }));
  }

  protected updateDriverGroups(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const groupIds = Array.from(select.selectedOptions)
      .map((option) => Number(option.value))
      .filter((value) => Number.isFinite(value));
    this.driverForm.update((current) => ({ ...current, groupIds }));
  }

  protected valueOrDash(value: string | null): string {
    return value?.trim() ? value : '-';
  }

  protected address(company: CompanySummary): string {
    const street = this.valueOrDash(company.street);
    const city = [company.postalCode, company.city].filter(Boolean).join(' ').trim();
    const parts = [street === '-' ? null : street, city || null].filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
  }

  private formFromCompany(company: CompanySummary): CreateCompanyRequest {
    return {
      name: company.name,
      street: company.street,
      postalCode: company.postalCode,
      city: company.city,
      country: company.country ?? 'Deutschland',
      contactName: company.contactName,
      contactEmail: company.contactEmail,
      contactPhone: company.contactPhone,
      concessionNumber: company.concessionNumber,
      shortCode: company.shortCode,
      vehicleCount: company.vehicleCount,
      paymentStatus: company.paymentStatus,
      paymentValidUntil: company.paymentValidUntil,
      packageType: company.packageType ?? 'Nicht festgelegt',
      enabledModules: company.enabledModules,
      internalPaymentNote: company.internalPaymentNote,
      status: company.status as CreateCompanyRequest['status']
    };
  }

  private normalizedRequest(): CreateCompanyRequest {
    const current = this.form();
    return {
      name: current.name.trim(),
      street: this.clean(current.street),
      postalCode: this.clean(current.postalCode),
      city: this.clean(current.city),
      country: this.clean(current.country) ?? 'Deutschland',
      contactName: this.clean(current.contactName),
      contactEmail: this.clean(current.contactEmail),
      contactPhone: this.clean(current.contactPhone),
      concessionNumber: this.clean(current.concessionNumber),
      shortCode: this.clean(current.shortCode),
      vehicleCount: current.vehicleCount ?? null,
      paymentStatus: this.clean(current.paymentStatus),
      paymentValidUntil: this.clean(current.paymentValidUntil),
      packageType: this.clean(current.packageType),
      enabledModules: this.clean(current.enabledModules),
      internalPaymentNote: this.clean(current.internalPaymentNote),
      status: current.status
    };
  }

  private normalizedDriverRequest(): CreateDriverRequest {
    const current = this.driverForm();
    return {
      firstName: this.clean(current.firstName) ?? '',
      lastName: this.clean(current.lastName) ?? '',
      email: this.clean(current.email)?.toLowerCase() ?? '',
      phone: this.clean(current.phone),
      personnelNumber: this.clean(current.personnelNumber),
      companyId: this.companyId,
      groupIds: current.groupIds,
      preferredLanguage: this.clean(current.preferredLanguage) ?? 'de',
      dateOfBirth: this.clean(current.dateOfBirth),
      licenseValidUntil: this.clean(current.licenseValidUntil),
      pScheinValidUntil: this.clean(current.pScheinValidUntil),
      statusValidUntil: this.clean(current.statusValidUntil),
      status: current.status ?? 'ACTIVE'
    };
  }

  private emptyForm(): CreateCompanyRequest {
    return {
      name: '',
      country: 'Deutschland',
      paymentStatus: 'Testphase',
      packageType: 'Nicht festgelegt',
      status: 'ACTIVE'
    };
  }

  private emptyDriverForm(): Omit<CreateDriverRequest, 'companyId'> {
    return { ...EMPTY_DRIVER_FORM };
  }

  private loadDriverGroups(): void {
    this.api.driverGroups().subscribe((groups) => this.driverGroups.set(groups));
  }

  protected driverGroupOptions(): DriverGroupOption[] {
    return this.driverGroups().filter((group) => group.companyId === this.companyId);
  }

  private clean(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
